import { useNavigate, useParams } from "react-router-dom";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { useMutation } from "@tanstack/react-query";
import {
  useTheme,
  Box,
  useMediaQuery,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  EditarObraFormData,
  editarObraSchema,
} from "./schema/editar-obra.schema";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import Input from "../../shared/components/formulario/input/input";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { obraService } from "./service/obra.service";
import { toast } from "react-toastify";
import useLoadingStore from "../../shared/store/loading.store";
import { toastError } from "../../shared/services/errors/erros-handler";
import { InputFileUpload } from "../../shared/components/formulario/input-file/inputFile";
import { useEffect, useState } from "react";
import { WorkStatusEnum } from "../../shared/utils/enum/enum";
import { formatDate } from "../../shared/utils";
import { EtapasDaObraFormPart } from "../../shared/components/formulario/parts/EtapasDaFormPart/etapasdaObraform.part";
import { CargosFormPart } from "../../shared/components/formulario/parts/CargosPrecoDaFormPart /CargosPrecoObraform.part";
import { SelectWorkStatus } from "../../shared/components/formulario/select/SelectWorkStatus";

const PAGINAS = [
  { label: "Dados básicos", value: 0 },
  { label: "Etapas da obra", value: 1 },
  { label: "Cargos e preços", value: 2 },
  // Futuras páginas podem ser adicionadas aqui
];

interface WorkStages {
  id: string;
  name: string;
  description?: string | null;
  startDate: string;
  endDate: string;
  percentage?: number;
  order?: number;
  status?: WorkStatusEnum;
}

export function EditarObra() {
  const methods = useForm<EditarObraFormData>({
    resolver: yupResolver(editarObraSchema),
  });

  const { setLoading } = useLoadingStore();
  const { id } = useParams<{ id: string }>();
  const [obra, setObra] = useState<any>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (id) {
      obraService
        .detalhar(id)
        .then((obra) => {
          setObra(obra);
          methods.reset({
            ...obra,
            idCompany: obra.idCompany,
            startDate: formatDate(obra.startDate),
            endDate: formatDate(obra.endDate),
            contractFile: obra.contractFile || "",
            workStages: obra.workStages.map((stage: WorkStages) => ({
              ...stage,
              startDate: formatDate(stage.startDate),
              endDate: formatDate(stage.endDate),
            })),
          });
        })
        .catch((error) => {
          toastError(error);
        });
    }
    // eslint-disable-next-line
  }, [id, methods]);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: EditarObraFormData) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      const { isEditValue, idCompany, ...dataLimpa } = data as any;
      await obraService.editar(id || "", dataLimpa);
    },
    onSuccess: () => {
      toast.success("Obra editada com sucesso");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = methods;

  const navigate = useNavigate();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BaseLayout title="Editar Obra">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={true}
        mostrarBotaoAdicional2={obra?.isMovement == 0}
        textoBotaoAdicional2="Adicionar Serviços adicionais"
        aoClicarBotaoAdicional2={() =>
          navigate(`/obra/servicos-adicionais/${id}`)
        }
        mostrarBotaoAdicional={obra?.isMovement > 0}
        textoBotaoAdicional="Gerenciar movimentações"
        iconBotaoAdicional="visibility"
        aoClicarBotaoAdicional={() =>
          navigate(`/obra/lista-servicos-adicionais/${id}`)
        }
        aoClicarBotaoVoltarESalvar={handleSubmit((formData) => {
          const { isEditValue, isMovement, ...dataLimpa } = formData as any;
          mutate(dataLimpa);
          navigate("/obras");
        })}
        aoClicarBotaoSalvar={handleSubmit((formData) => {
          const { isEditValue, isMovement, ...dataLimpa } = formData as any;
          mutate(dataLimpa);
        })}
        mostraBotaoVoltar
        aoClicarBotaoVoltar={() => navigate("/obras")}
      />

      {/* Navegação moderna usando Tabs */}
      <Tabs
        value={step}
        onChange={(_, newValue) => setStep(newValue)}
        sx={{
          borderBottom: "1px solid #eee",
          mb: 2,
          ".MuiTab-root": { textTransform: "none", fontWeight: 500 },
        }}
        variant={mdDown ? "scrollable" : "standard"}
        scrollButtons={mdDown ? "auto" : false}
      >
        {PAGINAS.map((pagina) => (
          <Tab key={pagina.value} label={pagina.label} value={pagina.value} />
        ))}
      </Tabs>

      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          const { isEditValue, ...dataLimpa } = formData as any;
          mutate(dataLimpa);
        })}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        {step === 0 && (
          <>
            <Box
              display="grid"
              gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
              gap="20px"
              width="100%"
              flexWrap="wrap"
            >
              <Input
                name="workName"
                label="Nome da Obra"
                methods={methods}
                required
              />

              <Controller
                name="idClient"
                control={control}
                render={({ field }) => (
                  <SelectCliente
                    value={field.value}
                    onChange={field.onChange}
                    disabled={obra?.isEditValue === false}
                  />
                )}
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr 1fr"}
              gap="20px"
              width="100%"
              flexWrap="wrap"
            >
              <InputMascarado
                name="totalValue"
                methods={methods}
                label="Valor Previsto"
                mask={mascaraMoedaReal}
                disabled={obra?.isEditValue === false}
              />

              <InputData
                name="startDate"
                label="Data de Início"
                methods={methods}
                disabled={obra?.isEditValue === false}
              />

              <InputData
                name="endDate"
                label="Previsão de Término"
                methods={methods}
                disabled={obra?.isMovement > 0}
              />

              <SelectWorkStatus name="status" methods={methods} />

              <Input
                name="percentageOfWork"
                label="Porcentagem geral da Obra % (0 a 100)"
                methods={methods}
                type="number"
                disabled
              />
            </Box>

            <EnderecoFormPart methods={methods} mdDown={mdDown} />

            <InputDescricao
              name="description"
              methods={methods}
              label="Descrição"
            />

            <InputFileUpload
              name="contractFile"
              label="Adicione um contrato nos seguintes formatos (PDF, JPG ou PNG)"
              methods={methods}
              required={true}
            />
          </>
        )}

        {step === 1 && (
          <Box>
            <EtapasDaObraFormPart
              methods={methods}
              showPercentageAndStatus={true}
            />
          </Box>
        )}

        {step === 2 && (
          <Box>
            <CargosFormPart methods={methods} />
          </Box>
        )}
      </Box>
    </BaseLayout>
  );
}
